import {
  QueryClient,
  QueryKey,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { featureViewQueryKeys } from "@/app/data/featureViewQueryKeys";
import { setSentryUser } from "@/app/utils/sentry";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import { setAllowTracking } from "@/common/utils/analytics/tracking";
import api from "@/common/utils/api";
import { ENVIRONMENT } from "@/common/utils/env";
import {
  isUsableArrayQueryData,
  QueryDataSource,
  reportInvalidArrayQueryData,
} from "@/common/utils/queryDataValidation";
import { segmentQueryKeys } from "@/company/data/segmentQueryKeys";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { BootstrapDTO } from "@reflag/shared/bootstrapAPI";
import { allowEmployeeTracking } from "@reflag/shared/utils/allowEmployeeTracking";

export function useBootstrapData(
  orgId?: string,
  envId?: string,
  enabled?: boolean,
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: commonQueryKeys.bootstrap(orgId),

    queryFn: () =>
      api
        .get<"/bootstrap">("/bootstrap", {
          params: {
            envId: envId,
            orgId: orgId,
          },
        })
        .then((res) => res.data)
        .then((data) =>
          bootstrapLoadingSideEffects(data, queryClient, "bootstrap-api"),
        ),
    refetchOnWindowFocus: false,
    retry: true,
    staleTime: Infinity,
    enabled: !!enabled,
  });
}

export type BootstrapDataSource = Extract<
  QueryDataSource,
  "bootstrap-api" | "server-bootstrap"
>;

function setBootstrapArrayQueryData(
  queryClient: QueryClient,
  queryKey: QueryKey,
  data: unknown,
  queryName: string,
  appId: string,
  source: BootstrapDataSource,
): void {
  if (!isUsableArrayQueryData(data)) {
    reportInvalidArrayQueryData(data, {
      queryName,
      source,
      context: { appId },
    });
    return;
  }

  queryClient.setQueryData(queryKey, data);
}

export function bootstrapLoadingSideEffects(
  bootstrapData: BootstrapDTO,
  queryClient: QueryClient,
  source: BootstrapDataSource,
): BootstrapDTO {
  setAllowTracking(
    allowEmployeeTracking(
      ENVIRONMENT,
      bootstrapData.user,
      bootstrapData.org?.id,
    ),
  );

  const { id: userId, email } = bootstrapData.user ?? {};

  if (userId) {
    setSentryUser({ id: userId, email });
  }

  // Set the app-level data in the query cache. Although the bootstrap payload
  // is statically typed, it is runtime input and must not poison the cache.
  for (const [appId, data] of Object.entries(bootstrapData.featureViews)) {
    setBootstrapArrayQueryData(
      queryClient,
      featureViewQueryKeys.list(appId),
      data,
      "featureViews",
      appId,
      source,
    );
  }

  for (const [appId, data] of Object.entries(bootstrapData.featureNames)) {
    setBootstrapArrayQueryData(
      queryClient,
      featureQueryKeys.listNames(appId),
      data,
      "featureNames",
      appId,
      source,
    );
  }

  for (const [appId, data] of Object.entries(bootstrapData.segments)) {
    setBootstrapArrayQueryData(
      queryClient,
      segmentQueryKeys.list(appId),
      data,
      "segments",
      appId,
      source,
    );
  }

  return bootstrapData;
}
